import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCcw, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const SupplierReturns = () => {
  const { user } = useAuth();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const supplierId = user?._id;
      
      if (!supplierId) {
        setLoading(false);
        return;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/get-supplier-orders/${supplierId}`
      );
      
      const allOrders = res.data || [];
      // Filter orders that have an exchange request
      const returnOrders = allOrders.filter(order => order.exchangeRequest?.isRequested);
      setReturns(returnOrders);
    } catch (err) {
      console.error("Error fetching returns:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  const filteredReturns = returns.filter(
    (order) =>
      order._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.exchangeRequest?.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredReturns.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const currentReturns = filteredReturns.slice(startIndex, startIndex + entriesPerPage);

  const SkeletonRow = () => (
    <tr className="border-b border-gray-100">
      {[...Array(6)].map((_, i) => (
        <td key={i} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case "Approved": return "bg-green-100 text-green-600";
      case "Requested": return "bg-blue-100 text-blue-600";
      case "Rejected": return "bg-red-100 text-red-600";
      case "Completed": return "bg-purple-100 text-purple-600";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <section className="min-h-screen p-6 bg-[#EEF3F7]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
          <RefreshCcw className="text-[#fba93d] w-7 h-7" />
          Returns Management
        </h1>
        <button
          onClick={fetchReturns}
          className="flex items-center gap-2 bg-[#ea8d2a] text-white px-4 py-2 rounded-lg hover:bg-orange-600"
        >
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by Order ID, Customer, or Reason..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-1 focus:ring-[#ea8d2a]"
          />
          <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Order ID</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Customer</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Date Requested</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Reason</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => <SkeletonRow key={i} />)
              ) : currentReturns.length > 0 ? (
                currentReturns.map((order) => (
                  <tr key={order._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{order._id.slice(-6).toUpperCase()}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {order.user ? `${order.user.firstName} ${order.user.lastName}` : "--"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {order.exchangeRequest?.requestedAt ? new Date(order.exchangeRequest.requestedAt).toLocaleDateString() : "--"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate" title={order.exchangeRequest?.reason}>
                      {order.exchangeRequest?.reason || "N/A"}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(order.exchangeRequest?.status)}`}>
                        {order.exchangeRequest?.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No return requests found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && filteredReturns.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredReturns.length)} of {filteredReturns.length} items
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SupplierReturns;
